import { useState } from 'react';

import { Button, Flex, Text, Input } from '@opendoor/bricks/core';
import { useObservability } from 'shared/helpers/observability';
import { showToast } from '@opendoor/bricks/complex/Toast';

import { text } from 'sellDirect/components/closingDash/content';
import { CloseDateFormContent } from 'sellDirect/components/closingDash/changeCloseDate/CloseDateFormContent';

import {
  IResetableFormProps,
  ResetableForm,
} from 'sellDirect/components/closingDash/ResetableForm';
import { useUpdateCloseEscrowDate } from 'sellDirect/graphql/useUpdateCloseEscrowDate';
import { ClosingTaskSubmissionEventName } from 'sellDirect/graphql/useUpdateClosingTasks';
import { LocalDate } from '@js-joda/core';
import { SHORT_FULL_DATE_FORMAT } from 'shared/helpers/datetime';
import { UnderContractOffer } from 'sellDirect/OfferContext';

export interface CloseDateFormProps {
  /** Offer in the under contract step */
  offer: UnderContractOffer;
  /** Available close dates to choose from */
  availableCloseDates: LocalDate[];
  /** Callback that directs the user to the summary view */
  goBackToSummary: () => void;
  /** Whether we can click a cancel button to go to the summary view */
  canCancel: boolean;
}

export interface CloseDateFormState {
  /**
   * This must be the in the form of `LocalDate.toString` ("2016-12-24" ISO 8601 format).
   *
   * We cannot use `LocalDate` because `ResetableForm` uses strict equality to compare values,
   * which does not work on `LocalDate`.
   */
  closeEscrowDate?: string;
}

/**
 * Form for requesting a new close date
 *
 * component-test-check-ignore storybook-check-ignore
 */
export const CloseDateForm: React.FC<CloseDateFormProps> = ({
  offer,
  availableCloseDates,
  goBackToSummary,
  canCancel,
}) => {
  const { trackEvent } = useObservability();

  const [formState, setFormState] = useState<CloseDateFormState>({
    closeEscrowDate: offer.closeEscrowDate.toString(),
  });

  const { updateCloseEscrowDate, loading } = useUpdateCloseEscrowDate({
    onCompleted: () => {
      trackEvent(ClosingTaskSubmissionEventName, 'update-close-date');
      goBackToSummary();
    },
    errorPolicy: 'none',
    onError: () =>
      showToast(
        'error',
        'Oops, something went wrong. Please try again in a few hours or contact your experience partner.',
        'cake-update-coe-date-error-toast',
        { toastOptions: { position: 'bottom-left' } },
      ),
  });

  const onSubmit: IResetableFormProps['onSubmit'] = (resetableForm) => {
    setFormState(resetableForm?.formState);
    trackEvent('click', 'change-escrow-date');
    updateCloseEscrowDate(resetableForm?.formState?.closeEscrowDate?.toString());
  };

  return (
    <ResetableForm<CloseDateFormState>
      initialFormState={formState}
      render={(state) => (
        <CloseDateFormContent
          shouldSeeAgentExperience={offer.shouldSeeAgentExperience}
          currentCloseEscrowDate={offer.closeEscrowDate}
          changeCloseEscrowDateDeadline={offer.changeCloseEscrowDateDeadline}
          availableCloseDates={availableCloseDates}
          loading={loading}
          resetableForm={state}
          onCancel={goBackToSummary}
          canCancel={canCancel}
        />
      )}
      onSubmit={onSubmit}
    />
  );
};

/** Content to show if the user cannot change the close date */
export const CannotChangeCloseDate = ({
  shouldSeeAgentExperience,
  closeEscrowDate,
}: {
  shouldSeeAgentExperience: boolean;
  closeEscrowDate: LocalDate;
}) => (
  <Flex flexDir="column" gridGap="9">
    <Input
      analyticsName="cake-close-date-form-close-date"
      id="disabled-change-close-date"
      type="text"
      label={`${shouldSeeAgentExperience ? 'Your client’s' : 'Your'} close date`}
      placeholder="Month, Day, Year"
      value={closeEscrowDate.format(SHORT_FULL_DATE_FORMAT)}
      disabled
      // Empty handler because this input is always disabled
      onChange={() => {}}
      unboundedWidth
    />
    <Text>{text.closingPage.pastDeadlineToChangeCloseOfEscrow(shouldSeeAgentExperience)}</Text>
    <Button
      disabled
      analyticsName=""
      aria-label="Next button is disabled since your close date can no longer be changed. Contact an experience partner for assistance. "
      // Empty handler because this button is always disabled
      onClick={() => {}}
    >
      Next
    </Button>
  </Flex>
);
